import Restart16 from '@carbon/icons-react/es/restart/16';
import { faExclamationTriangle } from '@fortawesome/pro-duotone-svg-icons';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import {
  Container,
  EmptyState,
  EmptyStateContainer as EmptyStateContainerAtom,
  EmptyStateIcon,
} from 'components/atoms/empty-state';
import { ROCKETLANE_EMAILS, RoutePaths } from 'constant';
import { useTranslation } from 'hooks/use-translation';

const EmptyStateContainer = styled(EmptyStateContainerAtom)`
  && {
    padding: var(--spacing-12);
  }
  ${Container} {
    width: auto;
    max-width: max-content;
  }
`;

const ErrorInfo = styled.div`
  margin-bottom: var(--spacing-06);
`;

const supportEmail = ROCKETLANE_EMAILS.support;
const domainName = window.location.hostname;

interface Props {
  error: Error;
}

const OktaTimeJWTErrors = [
  'The JWT was issued in the future',
  'The JWT expired and is no longer valid',
];

export const CallbackErrorPage: FC<Props> = ({ error }) => {
  const mailSubject = useTranslation('okta.callback_error.email.subject', {
    domainName,
  });
  const mailContent = useTranslation('okta.callback_error.email.content', {
    domainName,
  });

  const ErrorMessage = OktaTimeJWTErrors.includes(error.message) ? (
    <FormattedMessage id="okta.callback_error.info_timezone" />
  ) : (
    <FormattedMessage id="okta.callback_error.info" />
  );

  return (
    <EmptyStateContainer $transparent>
      <EmptyState
        image={<EmptyStateIcon icon={faExclamationTriangle} />}
        title={<FormattedMessage id="okta.callback_error.title" />}
        message={
          <>
            <ErrorInfo>
              <FormattedMessage id="failure" /> : {error.message}
            </ErrorInfo>
            {ErrorMessage}
          </>
        }
        primaryCTA={
          <EmptyState.CallToAction.Primary
            href={RoutePaths.login}
            icon={<Restart16 />}
          >
            <FormattedMessage id="okta.callback_error.cta" />
          </EmptyState.CallToAction.Primary>
        }
        secondaryCTA={
          <FormattedMessage
            id="okta.callback_error.info_extra"
            values={{
              link: (
                <a
                  target="_blank"
                  href={`mailto:${supportEmail}?subject=${mailSubject}&body=${mailContent}`}
                  rel="noreferrer"
                >
                  {supportEmail}
                </a>
              ),
            }}
          />
        }
      />
    </EmptyStateContainer>
  );
};
